import type { defineComponent } from "vue";
import {
  createRouter,
  createWebHistory,
  type NavigationGuardReturn,
  type RouteRecordRaw,
} from "vue-router";

import appConfig from "@appSrc/../appConfig";
import { RouteName, RouteView } from "@shared/types/routes";
import { queryParamSanitizationGuard } from "./guards/query-param-sanitzation-guard";
import {
  allowedRoutes,
  getAllowedNavigationRoutes,
  getLandingPage,
} from "./helpers";
import { CONFIG } from "@shared/config";
import {
  getProductModeFromPath,
  PLAN_PATH,
  TRUCK_PLAN_PATH,
} from "@shared/utils/product-mode";
import { useUIStore } from "@shared/store/ui";
import type { NavigationItem } from "@shared/store/ui/types";
import { getNormalizedQueryParams } from "@shared/utils/query-params";
import logger from "@shared/utils/logger";

const {
  application: {
    initialization: { productModes },
  },
  features: {
    rangeSpider: {
      additionalVehicles: { maxVehicles },
    },
  },
} = CONFIG;

type LazyLoadedComponent = Promise<ReturnType<typeof defineComponent>>;

const { BASE_URL } = import.meta.env;

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: RouteName.ROOT,
    component: (): LazyLoadedComponent =>
      import("@shared/views/index/Index.vue"),
    children: [
      {
        path: "",
        name: RouteName.INDEX,
        components: {
          [RouteView.PRIMARY]: () =>
            import("@shared/views/primary-panel/PrimaryPanel.vue"),
          [RouteView.PRIMARY_CONTENT]: () =>
            import("@shared/views/search/Search.vue"),
        },
        children: [
          {
            path: "",
            redirect: (to) => ({ name: getLandingPage(), query: to.query }),
          },
          {
            path: "/search",
            name: RouteName.SEARCH,
            meta: {
              title: "Search",
              bottomSheet: {
                hasTopOffset: false,
                snapIndex: 1,
                offsetBottom: [62, 220],
              },
            },
            components: {
              [RouteView.PRIMARY_CONTENT]: () =>
                import("@shared/views/search/Search.vue"),
            },
          },
          {
            path: "/search/:placeId?",
            name: RouteName.SEARCH_DETAILS,
            props: true,
            meta: {
              title: "Place details",
            },
            components: {
              [RouteView.SECONDARY_CONTENT]: () =>
                import("@shared/views/search/SearchDetail.vue"),
            },
            // Because the `placeId` parameter is optional we want to check query params to see if we need to navigate to search or search details.
            beforeEnter: (to): NavigationGuardReturn => {
              if (to.params.placeId) return;

              if (!to.query.searchCenter)
                return { name: RouteName.SEARCH, query: to.query };
            },
          },
          {
            path: "/plan-route",
            // NOTE: Only for those using legacy links to this page
            alias: ["/plan-trip", "/plan-journey"],
            name: RouteName.PLAN_ROUTE,
            meta: { title: "Plan Route" },
            components: {
              [RouteView.PRIMARY_CONTENT]: (): LazyLoadedComponent =>
                import("@shared/views/plan-route/PlanRoute.vue"),
            },
          },
          {
            path: "/plan-truck-route",
            name: RouteName.PLAN_TRUCK_ROUTE,
            meta: { title: "Plan Route" },
            components: {
              [RouteView.PRIMARY_CONTENT]: (): LazyLoadedComponent =>
                import("@shared/views/plan-route/PlanRoute.vue"),
            },
          },
          {
            path: "/plan-route/:routeId",
            name: RouteName.ROUTE_OVERVIEW_ROOT,
            redirect: () => ({ name: RouteName.ROUTE_OVERVIEW_DETAILS }),
            props: true,
            alias: [
              "/reroute/:routeId",
              "/route/:routeId",
              "/plan-truck-route/:routeId",
            ],
            components: {
              [RouteView.SECONDARY_CONTENT]: () =>
                import("@shared/views/route-overview/index.vue"),
            },
            children: [
              {
                path: "",
                name: RouteName.ROUTE_OVERVIEW_DETAILS,
                props: true,
                component: () =>
                  import("@shared/views/route-overview/details/RouteOverview.vue"),
                children: [
                  {
                    path: "send-to-phone",
                    name: RouteName.ROUTE_OVERVIEW_DETAILS_SEND_TO_PHONE,
                    props: true,
                    component: () =>
                      import("@shared/views/route-overview/details/subviews/send-to-phone/SendToPhone.vue"),
                  },
                  {
                    path: "amenity-charge-stop",
                    name: RouteName.ROUTE_OVERVIEW_DETAILS_AMENITY_CHARGE_STOP,
                    props: true,
                    component: () =>
                      import("@shared/views/route-overview-amenity-charge-stop/RouteOverviewAmenityChargeStop.vue"),
                  },
                  {
                    path: "ai-enhanced",
                    name: RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED,
                    props: true,
                    component: () =>
                      import("@shared/views/route-overview-ai-enhanced/RouteOverviewAiEnhanced.vue"),
                  },
                  {
                    path: "location/:viaId",
                    name: RouteName.ROUTE_OVERVIEW_DETAILS_AI_ENHANCED_LOCATION,
                    props: true,
                    component: () =>
                      import("@shared/views/route-overview-ai-enhanced-location/RouteOverviewAiEnhancedLocation.vue"),
                  },
                ],
              },
              {
                path: "stations/:stationId",
                name: RouteName.ROUTE_OVERVIEW_STATION_ROOT,
                redirect: { name: RouteName.ROUTE_OVERVIEW_STATION },
                props: true,
                component: () => import("@shared/views/station/index.vue"),
                children: [
                  {
                    path: "",
                    name: RouteName.ROUTE_OVERVIEW_STATION,
                    props: true,
                    component: () =>
                      import("@shared/views/station/details/Station.vue"),
                  },
                  {
                    path: "charge-cards",
                    name: RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS_ROOT,
                    redirect: { name: RouteName.STATION_CHARGE_CARDS },
                    props: true,
                    component: () =>
                      import("@shared/views/station/child/index.vue"),
                    children: [
                      {
                        path: "",
                        props: true,
                        name: RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS,
                        component: () =>
                          import("@shared/views/station/charge-card/StationChargeCard.vue"),
                      },
                      {
                        path: "charge-cards/pricing/:priceId",
                        name: RouteName.ROUTE_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
                        props: true,
                        component: () =>
                          import("@shared/views/station/pricing/StationPricing.vue"),
                      },
                    ],
                  },
                  {
                    path: "pricing/:priceId",
                    name: RouteName.ROUTE_OVERVIEW_STATION_PRICING_ROOT,
                    props: true,
                    component: () =>
                      import("@shared/views/station/child/index.vue"),
                    children: [
                      {
                        path: "",
                        name: RouteName.ROUTE_OVERVIEW_STATION_PRICING,
                        props: true,
                        component: () =>
                          import("@shared/views/station/pricing/StationPricing.vue"),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "/range-spider",
            name: RouteName.RANGE_SPIDER,
            meta: { title: "Range Spider" },
            components: {
              [RouteView.PRIMARY_CONTENT]: (): LazyLoadedComponent =>
                import("@shared/views/range-spider/RangeSpider.vue"),
            },
          },
          {
            path: "/range-spider/:rangeSpiderIds+",
            props: true,
            name: RouteName.RANGE_SPIDER_OVERVIEW,
            beforeEnter: (to): NavigationGuardReturn => {
              const ids = to.params.rangeSpiderIds;
              const totalComparisons = 1 + maxVehicles;

              // Return the user to the parent page if more than the max
              // comparisons were given as params.
              if (Array.isArray(ids) && ids.length > totalComparisons) {
                return { name: RouteName.RANGE_SPIDER };
              }

              return true;
            },
            components: {
              [RouteView.SECONDARY_CONTENT]: () =>
                import("@shared/views/range-spider-overview/RangeSpiderOverview.vue"),
            },
            children: [
              {
                path: "stations/:stationId",
                name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_ROOT,
                redirect: { name: RouteName.RANGE_SPIDER_OVERVIEW_STATION },
                props: true,
                components: {
                  [RouteView.RANGE_SPIDER_OVERVIEW]: (): LazyLoadedComponent =>
                    import("@shared/views/station/index.vue"),
                },

                children: [
                  {
                    path: "",
                    name: RouteName.RANGE_SPIDER_OVERVIEW_STATION,
                    props: true,
                    component: () =>
                      import("@shared/views/station/details/Station.vue"),
                  },

                  {
                    path: "charge-cards",
                    name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS_ROOT,
                    redirect: { name: RouteName.STATION_CHARGE_CARDS },
                    props: true,
                    component: () =>
                      import("@shared/views/station/child/index.vue"),
                    children: [
                      {
                        path: "",
                        props: true,
                        name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS,
                        component: () =>
                          import("@shared/views/station/charge-card/StationChargeCard.vue"),
                      },
                      {
                        path: "charge-cards/pricing/:priceId/:power?",
                        name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_CHARGE_CARDS_PRICING,
                        props: true,
                        component: () =>
                          import("@shared/views/station/pricing/StationPricing.vue"),
                      },
                    ],
                  },

                  {
                    path: "pricing/:priceId/:power?",
                    name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_PRICING_ROOT,
                    props: true,
                    component: () =>
                      import("@shared/views/station/child/index.vue"),
                    children: [
                      {
                        path: "",
                        name: RouteName.RANGE_SPIDER_OVERVIEW_STATION_PRICING,
                        props: true,
                        component: () =>
                          import("@shared/views/station/pricing/StationPricing.vue"),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "stations/:stationId",
            name: RouteName.STATION_ROOT,
            props: true,
            redirect: { name: RouteName.STATION },
            components: {
              [RouteView.SECONDARY_CONTENT]: () =>
                import("@shared/views/station/index.vue"),
            },
            children: [
              {
                path: "",
                name: RouteName.STATION,
                props: true,
                component: () =>
                  import("@shared/views/station/details/Station.vue"),
              },
              {
                path: "charge-cards",
                name: RouteName.STATION_CHARGE_CARDS_ROOT,
                redirect: { name: RouteName.STATION_CHARGE_CARDS },
                props: true,
                meta: { redirectTo: RouteName.STATION_ROOT },
                component: () =>
                  import("@shared/views/station/child/index.vue"),
                children: [
                  {
                    path: "",
                    props: true,
                    name: RouteName.STATION_CHARGE_CARDS,
                    component: () =>
                      import("@shared/views/station/charge-card/StationChargeCard.vue"),
                  },
                  {
                    path: "charge-cards/pricing/:priceId",
                    name: RouteName.STATION_CHARGE_CARDS_PRICING,
                    props: true,
                    component: () =>
                      import("@shared/views/station/pricing/StationPricing.vue"),
                  },
                ],
              },
              {
                path: "pricing/:priceId",
                name: RouteName.STATION_PRICING_ROOT,
                props: true,
                component: () =>
                  import("@shared/views/station/child/index.vue"),
                children: [
                  {
                    path: "",
                    name: RouteName.STATION_PRICING,
                    props: true,
                    component: () =>
                      import("@shared/views/station/pricing/StationPricing.vue"),
                  },
                ],
              },
            ],
          },
        ],
      },

      // NOTE: Redirect users from the legacy `more` page to the new modal.
      {
        path: "more",
        redirect: "/?modal=more",
        children: [
          {
            path: "units",
            redirect: "/?modal=more/localization",
          },
          {
            path: "currency",
            redirect: "/?modal=more/localization",
          },
          {
            path: "legend",
            redirect: "/?modal=more/legend",
          },
          {
            path: "faq",
            redirect: "/?modal=more/faq",
          },
          {
            path: "whats-new",
            redirect: "/?modal=more/whats-new",
          },
          {
            path: "acknowledgements",
            redirect: "/?modal=more/open-source-registry",
          },
          {
            path: "terms-ofuse",
            redirect: "/?modal=more/terms-of-use",
          },
          {
            path: "privacy-policy",
            redirect: "/?modal=more/privacy-policies",
          },
        ],
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: RouteName.NOT_FOUND,
    redirect: (to) => ({ name: getLandingPage(), query: to.query }),
  },
];

const router = createRouter({
  history: createWebHistory(BASE_URL),
  routes,
});

router.beforeEach(queryParamSanitizationGuard);

router.beforeEach((to) => {
  // Apps that don't support the commercial product mode cannot use the truck planner.
  if (
    !productModes.includes("commercial") &&
    getProductModeFromPath(to.path) === "commercial"
  ) {
    return {
      path: to.path.replace(TRUCK_PLAN_PATH, PLAN_PATH),
      query: to.query,
    };
  }

  const uiStore = useUIStore();
  const { navigation } = uiStore;
  const navigationQueryParam = getNormalizedQueryParams<NavigationItem>(
    to.query.navigation,
  );

  // Check the store as well, in case it got unset by our query params.
  // We want to always retain the navigation that was allowed during page init.
  // First time the store will be empty in the guard and thus we rely on the query param
  const normalizedNavigation = navigation.length
    ? navigation
    : navigationQueryParam;

  // If we are using the navigation query param, we need to redirect if needed.
  // This is due to the fact the the navigation query param hides all the navigation.
  if (normalizedNavigation) {
    const allowedRoutesByParam = [
      ...getAllowedNavigationRoutes(normalizedNavigation),
    ];

    if (allowedRoutesByParam.length) {
      // If we are redirected to the URL already we no longer need to do it.
      if (!allowedRoutesByParam.includes(to.name as RouteName)) {
        const redirectToFirstProductUrl = allowedRoutesByParam[0];

        return {
          name: redirectToFirstProductUrl,
          query: { ...to.query },
        };
      }
    }
  }

  // Redirect to landing page if route is not allowed by products or addons configuration.
  // This will also fire after we update based on query param.
  if (!allowedRoutes.includes(to.name as RouteName)) {
    const landingPage = getLandingPage();

    logger.warn(
      `Route: ${String(to.name)} is not allowed, redirecting to ${landingPage}`,
    );

    return { name: landingPage, query: { ...to.query, navigation: undefined } };
  }

  // Set page title, if a `meta.title` property exists
  if (appConfig?.title && typeof to?.meta?.title === "string") {
    document.title = `${appConfig.title} - ${to.meta.title}`;
  } else if (appConfig?.title) {
    document.title = appConfig.title;
  }

  // renamed map-legend to legend, redirect old urls
  if (to.query.modal === "more/map-legend") {
    return {
      ...to,
      query: {
        ...to.query,
        modal: "more/legend",
      },
    };
  }
});

// Persist the dbCountry query param (only when config.application.initialization.stationDatabasesPerCountry is set), otherwise we get edge cases.
router.beforeEach((to) => {
  const uiStore = useUIStore();

  if (to.query.dbCountry || !uiStore.countryDatabase) return;

  return {
    ...to,
    query: {
      ...to.query,
      dbCountry: uiStore.countryDatabase,
    },
  };
});

export default router;
